// src/components/ui/GoogleAddressAutocomplete.tsx
import React, { useState, useRef, useEffect } from "react";
import usePlacesAutocomplete, {
    getGeocode,
    getLatLng,
} from "use-places-autocomplete";
import { useLoadScript } from "@react-google-maps/api";
import { MapPin, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

// Definiamo le librerie staticamente
const libraries: ("places")[] = ["places"];

interface AddressData {
    address: string;
    city: string;
    zip: string;
    country: string;
    lat: number;
    long: number;
}

interface GoogleAddressAutocompleteProps {
    onAddressSelect: (data: AddressData) => void;
    // Questa è la prop fondamentale che mancava
    onInputChange: (value: string) => void;
    defaultValue?: string;
    className?: string;
}

// 1. WRAPPER (Carica lo script)
const GoogleAddressAutocomplete: React.FC<GoogleAddressAutocompleteProps> = ({
    onAddressSelect,
    onInputChange, // Riceviamo la prop qui
    defaultValue = "",
    className = "",
}) => {
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
        libraries,
        language: "it",
    });

    if (!isLoaded) return <InputSkeleton className={className} />;

    // Passiamo tutte le props, inclusa onInputChange, al componente interno
    return (
        <PlacesAutocomplete
            onAddressSelect={onAddressSelect}
            onInputChange={onInputChange} // Passiamo la prop qui
            defaultValue={defaultValue}
            className={className}
        />
    );
};

// 2. COMPONENTE INTERNO (Logica)
const PlacesAutocomplete: React.FC<GoogleAddressAutocompleteProps> = ({
    onAddressSelect,
    onInputChange, // Destrutturiamo la prop qui per poterla usare
    defaultValue,
    className,
}) => {
    const {
        ready,
        value,
        suggestions: { status, data },
        setValue,
        clearSuggestions,
    } = usePlacesAutocomplete({
        requestOptions: { componentRestrictions: { country: "it" } },
        debounce: 300,
        defaultValue,
    });

    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newVal = e.target.value;
        setValue(newVal);

        // QUI CHIAMIAMO LA FUNZIONE PASSATA DAL GENITORE
        if (onInputChange) {
            onInputChange(newVal);
        }

        setIsOpen(true);
    };

    const handleSelect = async (addressDescription: string) => {
        // Impostiamo il valore temporaneo per fermare l'input
        setValue(addressDescription, false);
        clearSuggestions();
        setIsOpen(false);

        try {
            const results = await getGeocode({ address: addressDescription });
            const { lat, lng: long } = await getLatLng(results[0]);

            const addressComponents = results[0].address_components;

            let city = "";
            let zip = "";
            let country = "";
            let route = "";
            let streetNumber = "";

            addressComponents.forEach((component) => {
                const types = component.types;
                if (types.includes("locality")) city = component.long_name;
                // Fallback per città
                if (!city && types.includes("administrative_area_level_3")) city = component.long_name;

                if (types.includes("postal_code")) zip = component.long_name;
                if (types.includes("country")) country = component.short_name;
                if (types.includes("route")) route = component.long_name;
                if (types.includes("street_number")) streetNumber = component.long_name;
            });

            // Costruiamo l'indirizzo pulito (Via + Civico)
            const cleanAddress = (route && streetNumber)
                ? `${route} ${streetNumber}`
                : (route ? route : addressDescription.split(',')[0]);

            // Sovrascriviamo l'input con l'indirizzo pulito
            setValue(cleanAddress, false);

            // Inviamo i dati completi al genitore
            onAddressSelect({
                address: cleanAddress,
                city,
                zip,
                country,
                lat,
                long,
            });
        } catch (error) {
            console.error("Errore Geocoding: ", error);
        }
    };

    return (
        <div ref={containerRef} className={`relative group ${className}`}>
            <MapPin
                className={`absolute left-4 top-1/2 -translate-y-1/2 transition-colors z-10 text-gray-400 group-focus-within:text-clip-500`}
                size={18}
            />
            <input
                value={value}
                onChange={handleInput}
                disabled={!ready}
                placeholder="Via Roma 10"
                className={`w-full pl-11 pr-4 py-3.5 bg-gray-50 border rounded-xl outline-none transition-all font-medium text-gray-900 placeholder:text-gray-400
            border-gray-200 focus:border-clip-500 focus:ring-2 focus:ring-clip-200
        `}
            />

            <AnimatePresence>
                {status === "OK" && isOpen && (
                    <motion.ul
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 5 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-gray-100 rounded-xl shadow-xl max-h-60 overflow-y-auto custom-scrollbar list-none p-1"
                    >
                        {data.map(({ place_id, description, structured_formatting }) => (
                            <li
                                key={place_id}
                                onClick={() => handleSelect(description)}
                                className="w-full flex items-center justify-between px-3 py-2.5 text-sm rounded-lg transition-colors cursor-pointer hover:bg-gray-50 text-gray-700"
                            >
                                <div className="flex flex-col text-left">
                                    <span className="font-medium text-gray-900">
                                        {structured_formatting.main_text}
                                    </span>
                                    <span className="text-xs text-gray-500">
                                        {structured_formatting.secondary_text}
                                    </span>
                                </div>
                            </li>
                        ))}
                    </motion.ul>
                )}
            </AnimatePresence>
        </div>
    );
};

// Skeleton per il caricamento
const InputSkeleton = ({ className }: { className?: string }) => (
    <div className={`relative ${className}`}>
        <Loader2 className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 animate-spin" size={18} />
        <div className="w-full h-[50px] bg-gray-100 border border-gray-200 rounded-xl" />
    </div>
);

export default GoogleAddressAutocomplete;